import { useMemo, useState } from 'react';
import { useAuth } from '../../../utils/Auth';
import { useNavigate } from 'react-router-dom';
import API from '../../../API';
import adminAPI from '../../../API/adminAPI';
import { useLocalStorage } from '../../../utils/hooks/useLocalStorage';
import { useUserPermissions } from '../../../utils/hooks/useUserPermissions';
import { filterCompleteConnectButtons } from '../../../lib/connectButtons';
import { getQrDataFieldType } from '../../../lib/getQrDataFieldType';
import { checkUrlValidity } from '../../../lib/checkUrlValidity';
import { ensureHttpProtocol } from '../../../lib/ensureHttpProtocol';
import styles from './SaveBtn.module.css';

export default function SaveBtn(props) {
    const { reference, ref, linking, parent } = props;
    const navigate = useNavigate();
    const { createLiquidQR, updateQR, linkQR } = API;

    const [, setStoredQrId ] = useLocalStorage('qr_id');
    const [, setStoredQrType ] = useLocalStorage('qr_type');
    
    const { orgObj, activeOrgId, qr_data, qr_options, fileHandler } = useAuth();
    const { canEditQR } = useUserPermissions();
    const { files, filesReset, logo, handleLogoFile } = fileHandler;

    const [ loading, setLoading ] = useState(false);
    const orgId = useMemo(() => activeOrgId || '', [activeOrgId]);
    const qrType = useMemo(() => qr_data?.data?.qr_type || '', [qr_data?.data?.qr_type]);
    const planId = useMemo(() => orgObj?.plan?.plan_id || '', [orgObj?.plan?.plan_id]);
    const qrDataType = useMemo(() => getQrDataFieldType(qrType), [qrType]);
    const filesLoading = useMemo(() => {
        const hasPendingCarouselImages = (
            ['liquid-poster', 'for-sale'].includes(qrType) &&
            qr_data?.data?.tracker?.files?.some((file) => file?.url?.startsWith('blob:'))
        );

        if (qr_options?.image && qr_options.image.startsWith('blob:')) {
            return true;
        } else if (hasPendingCarouselImages) {
            return true;
        } else if (!['pdf','asset'].includes(qrDataType) || qr_data.data[qrDataType]?.files?.every(file => !file.url.startsWith('blob:'))) {
            return false;
        } else return true;
    }, [qrType, qrDataType, qr_data?.data?.tracker?.fields, qr_data?.data?.[qrDataType]?.files, qr_options?.image]);

    const disabled = useMemo(() => {
        const nicknameValid = qr_data?.data?.nickname?.length > 0;
        if (!nicknameValid || filesLoading || !canEditQR) return true;
        if (linking && nicknameValid) return false;

        switch (qrType) {
            case 'liquid_link':
                return !(qr_data?.data?.nickname && parent?.obj);
            case 'connect':
                return (!qr_data?.data?.connect?.defaultUrl || !checkUrlValidity(qr_data?.data?.connect?.defaultUrl));
            case 'pdf':
            case 'user-manual':
            case 'brochure':
            case 'menu-pdf':
                return !qr_data?.data?.pdf?.files?.length || (qr_data.data.pdf.files.every(file => file.name) ? false : true);
            case 'microsite':
            case 'landing-page':
                return !(planId !== 0 && qr_data?.data?.microsite);
            case 'location':
                return !(planId !== 0 && qr_data?.data?.location?.lat && qr_data?.data?.location?.lng);
            case 'bitsignal':
            case 'dbc':
                return !(qr_data?.data?.bitsignal?.uid?.length > 0 && qr_data?.data?.bitsignal?.authenticated === true);
            case 'calendly':
            case 'google-calendar':
                return !(planId !== 0 && qr_data?.data?.redirect_url && checkUrlValidity(ensureHttpProtocol(qr_data?.data?.redirect_url)) && (qr_data?.data?.redirect_url.includes('calendly') || qr_data?.data?.redirect_url.includes('calendar.google')));
            case 'assets':
            case 'liquid-label':
            case 'liquid-poster':
            case 'for-sale':
            case 'inventory':
            case 'tracker':
            case 'survey':
            case 'review':
            case 'workout-log':
            case 'service-log':
            case 'quiz':
            case 'maintenance-tracker':
            case 'vehicle-log':
            case 'container-label':
            case 'garden-label':
            case 'contact-form':
                return false;
            case 'google-review':
            case 'appointment':
            case 'music':
            case 'youtube':
            case 'tiktok':
            case 'linkedin':
            case 'link':
            case 'menu-link':
            case 'review-link':
            default:
                return !qr_data?.data?.redirect_url || !checkUrlValidity(ensureHttpProtocol(qr_data?.data?.redirect_url));
        };
    },[qr_data, qrType, planId, filesLoading, parent, linking]);

    const handleSurveyScaleValue = num => {
        if (!Number.isInteger(num)) return 5;
        return Math.min(Math.max(num, 3), 10);
    };

    const handleLinkSave = async () => {
        if (!qr_data?.data?.nickname || !parent?.obj) return;
        if (!canEditQR) {
            alert('You do not have permission to edit QR codes.');
            return;
        }
        if (qr_data?.data?.org_id && orgId && qr_data.data.org_id !== orgId) {
            alert('This QR belongs to a different organization. Switch organizations to edit it.');
            return;
        }
        try {
            setLoading(true);
            const date = new Date();

            let isNewRetail = false;

            if (qr_data.id.includes("-") && !qr_data.data?.org_id && !qr_data?.data?.created_at) {
                isNewRetail = true;
            }

            const linkedObj = {
                id: qr_data?.id,
                data: {
                    ...qr_data.data,
                    active: true,
                    qr_type: 'liquid_link',
                    linked_qr: parent?.obj?.id,
                    qr_style: {
                        ...qr_options,
                        height: 300,
                        width: 300,
                        image: qr_options?.image?.split('?')[0],
                    },
                    org_id: qr_data?.data?.org_id || orgId,
                    schedule: null,
                    created_at: date,
                    updated_at: date,
                }
            };

            const parentObj = {
                id: parent?.obj?.id,
                data: {
                    ...parent?.obj?.data,
                    children: [...(parent?.obj?.data?.children || []), qr_data?.id],
                    updated_at: date,
                }
            };

            const dataToSend = {
                linkData: linkedObj.data,
                pid: parentObj.id,
                parentData: parentObj.data,
            };

            if (logo?.file) {
                await handleLogoFile(qr_data?.id);
                dataToSend.linkData.qr_style.image = `https://storage.googleapis.com/${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}/qr_logos/${qr_data?.id}/logo.png`;
            }

            if (isNewRetail) {
                await adminAPI.initializeRetailCode(qr_data?.id);
            }

            await linkQR(qr_data?.id, dataToSend);
            setStoredQrId(qr_data?.id);
            navigate(`/collection/${qr_data?.id}/details`);
        } catch (err) {
            console.error('Error linking QR:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveQr = async () => {
        if (linking) {
            await handleLinkSave();
            return;
        }
        if (!canEditQR) {
            alert('You do not have permission to edit QR codes.');
            return;
        }
        if (qr_data?.data?.org_id && orgId && qr_data.data.org_id !== orgId) {
            alert('This QR belongs to a different organization. Switch organizations to edit it.');
            return;
        }
        try {
            setLoading(true);
            const date = new Date();

            let isNewRetail = false;

            if (qr_data.id.includes("-") && !qr_data.data?.org_id && !qr_data?.data?.created_at) {
                isNewRetail = true;
            }

            const dataToSave = {
                ...qr_data.data,
                active: true,
                created_at: qr_data?.data?.created_at || date,
                org_id: qr_data?.data?.org_id || orgId,
                qr_style: { ...qr_options, height: 300, width: 300 },
                redirect_url: ensureHttpProtocol(qr_data?.data?.redirect_url),
                updated_at: date,
            };

            if (qr_data?.data?.survey) dataToSave.survey = { ...qr_data?.data?.survey, scale: handleSurveyScaleValue(qr_data?.data?.survey.scale) };
            if (qrType === 'connect') {
                dataToSave.connect = {
                    ...dataToSave.connect,
                    buttons: filterCompleteConnectButtons(dataToSave?.connect?.buttons),
                };
            }

            const apiObj = {
                qrDataType,
                files,
                data: dataToSave
            };

            // API calls
            if (isNewRetail) {
                await adminAPI.initializeRetailCode(qr_data?.id);
            }
            if (reference === 'new' || isNewRetail) {
                await createLiquidQR(qr_data?.id, apiObj);
            } else {
                await updateQR(qr_data?.id, apiObj);

                if (ref === 'bs') {
                    window.opener.postMessage({ type: 'edit', success: true, qr_style: dataToSave.qr_style }, process.env.REACT_APP_BS_ORIGIN);
                    window.close();
                    return;
                };
            };

            filesReset();
            setStoredQrId(qr_data?.id);
            setStoredQrType(null);

            // If creating a new Connect QR, go straight to Widgets Generator
            if (reference === "new" && qrType === "connect") {
                navigate('/widgets', { state: { qrId: qr_data?.id, fromNewCollection: true } });
            } else if (ref && ref !== 'null') {
                navigate(decodeURI(ref));
            } else {
                navigate(`/collection/${qr_data?.id}/details`);
            };
        } catch (err) {
            if (reference === 'bs') {
                window.opener.postMessage({ type: 'edit', success: false, qr_style: {} }, process.env.REACT_APP_BS_ORIGIN);
                window.close();
            };
            console.error(err);
        } finally {
            setLoading(false);
        };
    };

    return (
        <button className={styles.btn} onClick={handleSaveQr} disabled={disabled || loading} >
            {loading ? <i className='fa-duotone fa-spinner fa-spin' /> : 'Finish'}
        </button>
    );
};
